<template>
  <ValidationObserver
    ref="applyFormRef"
    vid="applyForm"
    tag="div"
  >
    <b-form v-if="showForm">
      <ValidationProvider
        v-slot="{ errors }"
        rules="required"
        name="Name"
        class="m-b-40"
        tag="div"
      >
        <label
          :for="`name`"
          class="required"
        >
          Full Name
        </label>
        <b-form-input
          id="name"
          v-model="form.name"
          class="form-input bg-white-1"
          type="text"
          :class="{'is-invalid': errors[0]}"
          lazy
        />
        <b-form-invalid-feedback v-if="errors[0]">
          {{ errors[0] }}
        </b-form-invalid-feedback>
      </ValidationProvider>
      <ValidationProvider
        v-slot="{ errors }"
        :rules="{
          required: true,
          email: true,
        }"
        name="email"
        class="m-b-40"
        tag="div"
      >
        <label
          :for="`email`"
          class="required"
        >
          Email
        </label>
        <b-form-input
          id="email"
          v-model="form.email"
          class="form-input bg-white-1"
          :class="{'is-invalid': errors[0]}"
          lazy
        />
        <b-form-invalid-feedback v-if="errors[0]">
          {{ errors[0] }}
        </b-form-invalid-feedback>
      </ValidationProvider>
      <ValidationProvider
        v-slot="{ errors }"
        class="m-b-40"
        :rules="{
          required: true,
          regex:phoneNumberRegex,
        }"
        name="phone"
        tag="div"
      >
        <label
          :for="`phone`"
          class="required"
        >
          Phone
        </label>
        <b-form-input
          id="phone"
          v-model="form.phone"
          class="form-input bg-white-1"
          :class="{'is-invalid': errors[0]}"
          lazy
        />
        <b-form-invalid-feedback v-if="errors[0]">
          {{ errors[0] }}
        </b-form-invalid-feedback>
      </ValidationProvider>
      <ValidationProvider
        v-slot="{ errors }"
        class="m-b-40"
        :rules="{
          urlChecking: true,
        }"
        name="Linkedin Profile link"
        tag="div"
      >
        <label
          :for="`lp-link`"
        >
          Linkedin Profile Link
        </label>
        <b-form-input
          id="lp-link"
          v-model="form.linkedin_profile"
          :class="{'is-invalid': errors[0]}"
          class="form-input bg-white-1"
          lazy
        />
        <b-form-invalid-feedback>
          {{ errors[0] }}
        </b-form-invalid-feedback>
      </ValidationProvider>
      <div class="m-b-40">
        <label
          :for="`cover-letter`"
        >
          Cover Letter
        </label>
        <b-form-textarea
          id="cover-letter"
          v-model="form.cover_letter"
          rows="4"
          class="bg-white-1"
          no-resize
        />
      </div>
      <div class="m-b-40">
        <label class="required">
          Upload CV / Resume
        </label>
        <file-pond
          ref="fileInput"
          :label-idle="`Drag & Drop your files or <span>Browse</span>`"
          max-file-size="5MB"
          :file-validate-type-detect-type="detectorFunction"
          :accepted-file-types="acceptedTypes"
          file-validate-type-label-expected-types="Expected pdf, doc, docx"
          :label-file-type-not-allowed="fileErrorMsg"
          required
          credits
        />
        <span
          v-if="fileErrorMsg"
          class="text-danger"
        >
          {{ fileErrorMsg }}
        </span>
      </div>
      <b-form-checkbox
        v-model="isAgree"
        class="m-b-50"
      >
        <span class="text-primary f-w-500 l-h-22 grey-dark required">
          By using this form you agree with the storage and handling of your data by this website.
        </span>
      </b-form-checkbox>
      <div class="d-flex justify-content-end mt-5">
        <b-btn
          id="submit-btn"
          variant="primary"
          class="btn-main-bg-blue btn-main-bg-blue-bordered"
          :disabled="disabled"
          @click.prevent="handleSubmit"
        >
          Submit
        </b-btn>
      </div>
    </b-form>
  </ValidationObserver>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import vueFilePond from 'vue-filepond';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import { mapActions } from 'vuex';
import { scrollToFirstOnValidationErrors, showNotification } from '@/helpers/helpers';

const phoneNumberRegex = /^[+]?[0-9]{9,24}$/;

const defaultForm = {
  name: '',
  email: '',
  phone: '',
  linkedin_profile: '',
  cover_letter: '',
  cv: '',
};

const FilePond = vueFilePond(FilePondPluginFileValidateType);
export default {
  components: {
    ValidationObserver,
    ValidationProvider,
    FilePond,
  },
  props: {
    formType: {
      type: String,
      default: '',
    },
    vacancyId: {
      type: Number,
      default: null,
    },
  },
  data: () => ({
    form: {
      ...defaultForm,
    },
    phoneNumberRegex,
    showForm: true,
    isAgree: false,
    disabled: false,
    fileErrorMsg: '',
    fileObject: null,
    acceptedTypes: [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
    ],
    extensions: ['.pdf', '.doc', '.docx'],
  }),
  methods: {
    ...mapActions([
      'submitApplyForm',
    ]),
    handleSubmit() {
      this.$refs.applyFormRef.validate().then(async (success) => {
        this.fileObject = this.$refs.fileInput?.getFile() || null;
        if (!success || !this.fileObject || !this.isAgree) {
          showNotification({
            ctx: this,
            variant: 'danger',
            title: 'Form Validation',
            message: 'Please check all mandatory fields',
          });
          this.fileErrorMsg = !this.fileObject ? 'CV is required' : '';
          scrollToFirstOnValidationErrors('submit-btn');
          return;
        }
        this.fileErrorMsg = '';
        this.loading = true;
        this.disabled = true;
        await this.submitApplyForm({
          body: {
            ...this.form,
            vacancy: this.vacancyId,
          },
          type: this.formType,
          notificationParams: {
            ctx: this,
            variant: 'success',
            title: 'Success',
            message: 'Form submitted successfully',
          },
        }).then(() => {
          this.loading = false;
          this.disabled = false;
          this.resetForm();
        }).catch(() => {
          this.loading = false;
          this.disabled = false;
        });
      });
    },
    base64(cv) {
      const reader = new FileReader();
      reader.readAsDataURL(cv);
      reader.onloadend = () => {
        const regex = '(.*)(base64,)(.*)';
        const macthes = reader.result.match(regex);
        this.form.cv = macthes[macthes.length - 1];
      };
    },
    detectorFunction(source, type) {
      return new Promise((resolve, reject) => {
        const ext = `.${source.name.split('.').pop().toLowerCase()}`;
        const fileName = source.name.slice(0, source.name.lastIndexOf('.'));
        const bCondition01 = (type === '') && this.extensions.includes(ext);
        const bCondition02 = this.acceptedTypes.includes(type);

        if (fileName.length > 200) {
          this.fileErrorMsg = 'CV file max name length is 200 characters';
          reject();
        }

        if (bCondition01 || bCondition02) {
          this.fileErrorMsg = '';
          this.base64(source);
          this.form.extension = ext;
          resolve(this.acceptedTypes[0]);
        } else {
          reject();
        }
      });
    },
    resetForm() {
      this.form = {
        ...defaultForm,
      };
      this.isAgree = false;
      this.showForm = false;
      this.$nextTick(() => { this.showForm = true; });
    },
  },
};
</script>
